import type { PlaidOAuthResumeInput, PlaidOAuthResumeRecord } from "./plaidOAuthResumeTypes"

export * from "./plaidOAuthResumeTypes"

const STORAGE_KEY = "plaidOAuthResume"
const RECORD_VERSION = 1

/**
 * Long enough for a bank app login, short enough that an abandoned attempt
 * cannot resurface in a later, unrelated visit.
 */
export const PLAID_OAUTH_RESUME_TTL_MS = 30 * 60 * 1000

let returnUri: string | null = null

/**
 * Must run before React Navigation syncs the URL, because Plaid needs the
 * exact URL it redirected to, including `oauth_state_id`.
 */
export function capturePlaidOAuthReturnUri(href: string) {
  try {
    returnUri = new URL(href).searchParams.has("oauth_state_id") ? href : null
  } catch {
    returnUri = null
  }
  if (!returnUri) {
    // Prunes an expired record (phone, verification id, link token) left by an abandoned attempt.
    loadPlaidOAuthResume()
  }
}

export function getPlaidOAuthReturnUri() {
  return returnUri
}

/** Plaid rejects an `oauth_state_id` it has already processed, so use it once. */
export function consumePlaidOAuthReturnUri() {
  returnUri = null
}

// localStorage rather than sessionStorage: the bank usually returns in a new tab.
function getStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage
  } catch {
    return null
  }
}

export function savePlaidOAuthResume(input: PlaidOAuthResumeInput, now = Date.now()) {
  const record: PlaidOAuthResumeRecord = { ...input, version: RECORD_VERSION, savedAt: now }
  try {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    // Quota or privacy mode: the return will show the "open your link again" screen.
  }
}

export function loadPlaidOAuthResume(now = Date.now()): PlaidOAuthResumeRecord | null {
  const storage = getStorage()
  const stored = storage?.getItem(STORAGE_KEY)
  if (!stored) {
    return null
  }

  try {
    const record = JSON.parse(stored) as PlaidOAuthResumeRecord
    const isValid =
      record.version === RECORD_VERSION &&
      typeof record.savedAt === "number" &&
      now - record.savedAt <= PLAID_OAUTH_RESUME_TTL_MS &&
      !!record.linkToken &&
      !!record.tokenId &&
      !!record.verificationId
    if (isValid) {
      return record
    }
  } catch {
    // Fall through and drop the unreadable record.
  }

  storage?.removeItem(STORAGE_KEY)
  return null
}

export function clearPlaidOAuthResume() {
  getStorage()?.removeItem(STORAGE_KEY)
}
